import { Order } from '@api/Orders/Order';
import { Reward } from '@api/Rewards/Reward';
import { ShoppingCartVariable } from '@api/ShoppingCart/ShoppingCart';
import { isOrderLineV2, OrderLine } from '@api/Orders/OrderLine';

export const getEarnedPoints = (order: Order, reward: Reward) => {
    let total = 0;

    if (order.products) {
        total += order.products.reduce((acc, product) => {
            return acc + product.cost;
        }, 0);
    }

    // Restar promoción y puntos usados (en pesos)
    total = Math.max(0, total - (order.promotion || 0) - (order.pointsAmount || 0));

    // points.cost = cuántos pesos cuestan X puntos
    // points.rewardPoints = cuántos puntos obtienes por ese costo
    //
    // ⚠️ Sin este guard, un `reward` a medio guardar (o con `cost` en 0) hacía
    // `total / 0` = **Infinity**, y eso se escribía a la cartera del cliente.
    // Nunca mordió porque la configuración real tiene cost 10, pero desde el
    // 2026-09-10 el SERVER también otorga puntos (webhook de Uber) y lee ese
    // documento sin validarlo. Agregado con la suite gemela de pruebas.
    if (!reward?.points?.cost || reward.points.cost <= 0) return 0;

    const pointsEarned = (total / reward.points.cost) * reward.points.rewardPoints;

    return Math.max(0, Math.floor(pointsEarned));
};

// Total real de una línea recalculado desde el snapshot: (precio base + modificadores) × cantidad.
// DUAL v1/v2. No mostrar importes con line.cost directo: en órdenes v1 creadas en krispis-web ese
// campo trae solo el precio base unitario (en POS trae el total de línea) — recalcular funciona
// para todas, incluida v2 (donde además valida unitPrice contra sus modifiers).
export const getOrderLineTotal = (line: OrderLine): number => {
    if (isOrderLineV2(line)) {
        const modifiersCost = (line.modifiers || []).reduce((sum, m) => sum + m.extraCost * m.quantity, 0);
        return (line.product.cost + modifiersCost) * line.quantity;
    }
    const variablesCost = (line.variables || []).reduce((sum, v) => sum + v.cost * v.quantity, 0);
    return (line.product.cost + variablesCost) * line.quantity;
};

// El catálogo viejo trae el precio incrustado en el nombre de opciones/extras
// ("Soda blueberry (+$50)"); junto al costo real calculado se vería doble.
// Limpiar SOLO para display — nunca reescribir el dato. Espejo en krispis-web
// (@shared/utils/OrderDisplay): cambiar uno exige cambiar el otro.
export const cleanOptionName = (name: string): string => {
    return name.replace(/\s*\(\s*\+?\s*\$\s*\d+(?:[.,]\d+)?\s*\)\s*$/, '').trim();
};

// Nombre limpio de la opción elegida de un modificador; '' si el snapshot no lo trae
// (p. ej. extraProducts sin productData hidratado) — el caller decide el fallback.
export const getModifierOptionName = (variable: ShoppingCartVariable): string => {
    const raw =
        variable.selectionOptions?.option ||
        variable.extraProducts?.productData?.name ||
        variable.extraIngredients?.ingredient ||
        '';
    return cleanOptionName(raw);
};

// ============================================================================
// RENDERER DUAL v1/v2 — representación canónica de una línea para display.
// TODAS las superficies (ticket WhatsApp, ModalViewOrder, tablero, y las
// pantallas 2.0 que vengan) renderizan desde OrderLineDisplay, nunca desde el
// formato crudo. ⚠️ PARIDAD: krispis-web replica este módulo en F3 (regla
// PointsHelper) — cambiar la forma aquí exige cambiarla allá.
// ============================================================================

export interface OrderLineDisplayModifier {
    label: string; // nombre del grupo/variable ("Sabor de salsas")
    optionName: string; // opción elegida, limpia ("BBQ"); '' si el snapshot v1 no la trae
    quantity: number;
    extraCostTotal: number; // costo extra × cantidad (0 = incluida en el precio)
}

export interface OrderLineDisplay {
    name: string;
    quantity: number;
    takeAway: boolean;
    note: string;
    total: number; // total de línea recalculado (getOrderLineTotal)
    modifiers: OrderLineDisplayModifier[];
    removedIngredients: string[]; // nombres para "Sin: ..."
    schemaVersion: 1 | 2;
}

// Ingredientes quitados: en v2 son explícitos; en v1 se derivan comparando la
// receta base del snapshot contra los inputs que quedaron seleccionados.
export const getRemovedIngredientNames = (line: OrderLine): string[] => {
    if (isOrderLineV2(line)) {
        return (line.removedIngredients || []).map((ingredient) => ingredient.name).filter((name) => !!name);
    }
    return (line.product?.inputs || [])
        .filter((input) => !line.inputs?.find((i) => i.inputId === input.inputId))
        .map((input) => input.inputData?.name || '')
        .filter((name) => name !== '');
};

export const getOrderLineDisplay = (line: OrderLine): OrderLineDisplay => {
    if (isOrderLineV2(line)) {
        return {
            name: line.product.name,
            quantity: line.quantity,
            takeAway: line.takeAway ?? false,
            note: line.note || '',
            total: getOrderLineTotal(line),
            modifiers: (line.modifiers || []).map((modifier) => ({
                label: modifier.groupName,
                optionName: cleanOptionName(modifier.optionName),
                quantity: modifier.quantity,
                extraCostTotal: modifier.extraCost * modifier.quantity
            })),
            removedIngredients: getRemovedIngredientNames(line),
            schemaVersion: 2
        };
    }
    return {
        name: line.product.name,
        quantity: line.quantity,
        takeAway: line.takeAway ?? false,
        note: line.note || '',
        total: getOrderLineTotal(line),
        modifiers: (line.variables || []).map((variable) => ({
            label: variable.name,
            optionName: getModifierOptionName(variable),
            quantity: variable.quantity,
            extraCostTotal: variable.cost > 0 ? variable.cost * variable.quantity : 0
        })),
        removedIngredients: getRemovedIngredientNames(line),
        schemaVersion: 1
    };
};
